import React, { useState, useEffect, useCallback } from 'react'
import { MECETable, MECEPillar, MECETactic, WhatIfScenario, TacticInclusionState, ImplementationStatus } from '../../../models'
import { omapApi } from '../../../services/omapApi'
import { QuickAskQuestionType } from '@utils/quickAskPromptBuilder'
import { useAppDispatch, useAppSelector } from '../../../state/hooks'
import { updateWorkspaceAsync, updateAnalysisReport } from '../../../state/slices/conversationsSlice'
import {
  selectScenarioState,
  selectActiveScenario,
  selectAllScenarios,
  updateTacticInclusion,
  createScenario as createScenarioAction,
  deleteScenario as deleteScenarioAction,
  renameScenario as renameScenarioAction,
  selectScenario as selectScenarioAction,
  toggleComparisonMode as toggleComparisonModeAction
} from '../../../state/slices/scenariosSlice'
import { MECETableRenderer } from '../visualizations/MECETableRenderer'
import { ScenarioSelector } from '@components/shared/ScenarioSelector'
import { ExportPlaybookModal } from '@components/shared/ExportPlaybookModal'
import { useIsSampleCompany, SAMPLE_COMPANY_TOOLTIP } from '@state/hooks/useIsSampleCompany'
import './MECETableEditorSimple.css'

// Phase 4.5: Quick Ask callback context
interface QuickAskTacticContext {
  tacticId: string
  tacticTitle: string
  tacticDescription: string
  pillarName: string
  supportingEvidence: string[]
}

interface MECETableEditorSimpleProps {
  data: MECETable
  conversationId: string
  onSaveSuccess?: () => void
  // Phase 4.5: Quick Ask callback (returns Promise to allow awaiting completion)
  onQuickAsk?: (
    prompt: string,
    questionText: string,
    questionType: QuickAskQuestionType,
    tacticContext: QuickAskTacticContext
  ) => Promise<void>
  // Phase 6.5: Scenario mode props
  isScenarioMode?: boolean
  onToggleScenarioMode?: () => void
  // Phase 1.1: Pillar selection for Strategy Map generation
  onPillarSelectionChange?: (selectedPillars: string[]) => void
  initialSelectedPillars?: string[]
  isSelectionModeEnabled?: boolean
  // Jan 2026: Strategy Map data for downstream impact warnings
  strategyMapData?: any  // Optional Strategy Map for checking tactic references
}

/**
 * Check if a tactic reference is used in the Strategy Map
 * Returns the initiative IDs that reference this tactic
 */
function checkTacticReferencedInStrategyMap(
  tacticRef: string,
  strategyMapData: any
): string[] {
  if (!strategyMapData?.coherence_index?.tactic_to_initiatives) {
    return []
  }
  return strategyMapData.coherence_index.tactic_to_initiatives[tacticRef] || []
}

export const MECETableEditorSimple: React.FC<MECETableEditorSimpleProps> = ({
  data,
  conversationId,
  onSaveSuccess,
  onQuickAsk,
  isScenarioMode = false,
  onToggleScenarioMode: _onToggleScenarioMode,  // Reserved for future use
  onPillarSelectionChange,
  initialSelectedPillars,
  isSelectionModeEnabled = false,
  strategyMapData  // Jan 2026: For downstream impact warnings
}) => {
  const [isEditMode, setIsEditMode] = useState(false)
  const isSampleCompany = useIsSampleCompany()
  const [draftPillars, setDraftPillars] = useState<MECEPillar[]>(data.pillars || [])
  const [isDirty, setIsDirty] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [validationErrors, setValidationErrors] = useState<string[]>([])
  const [syncStatus, setSyncStatus] = useState<'idle' | 'syncing' | 'synced' | 'failed'>('idle')

  // Phase 1.1: Pillar selection state
  const [selectedPillars, setSelectedPillars] = useState<Set<string>>(() => {
    // Initialize with all pillars selected, or from initial props
    if (initialSelectedPillars) {
      return new Set(initialSelectedPillars)
    }
    return new Set(data.pillars?.map(p => p.name) || [])
  })
  const [isSelectionMode, setIsSelectionMode] = useState(false)

  // Phase 3.1: Reorder mode state
  const [isReorderMode, setIsReorderMode] = useState(false)

  // Phase 3.4: Export playbook modal state
  const [isExportModalOpen, setIsExportModalOpen] = useState(false)

  const dispatch = useAppDispatch()
  const activeConversation = useAppSelector(state =>
    state.conversations.conversations.find(c => c.id === conversationId)
  )

  // Phase 6.5: Scenario state
  const scenarioState = useAppSelector(state => selectScenarioState(state, conversationId))
  const activeScenario = useAppSelector(state => selectActiveScenario(state, conversationId))
  const scenarios = useAppSelector(state => selectAllScenarios(state, conversationId))
  const comparisonMode = scenarioState?.comparison_mode || false

  // Update draft when data changes
  useEffect(() => {
    if (!isEditMode) {
      setDraftPillars(data.pillars || [])
    }
  }, [data, isEditMode])

  // Phase 1.1: Sync pillar selection with parent
  useEffect(() => {
    onPillarSelectionChange?.(Array.from(selectedPillars))
  }, [selectedPillars, onPillarSelectionChange])

  // Phase 1.1: Update selection when data changes
  useEffect(() => {
    if (data.pillars && data.pillars.length > 0) {
      // Add any new pillars to selection
      setSelectedPillars(prev => {
        const newSet = new Set(prev)
        data.pillars.forEach(p => {
          if (!prev.has(p.name)) {
            newSet.add(p.name)
          }
        })
        return newSet
      })
    }
  }, [data.pillars])

  // Phase 1.1: Toggle pillar selection
  const handlePillarSelectionToggle = (pillarName: string) => {
    setSelectedPillars(prev => {
      const newSet = new Set(prev)
      if (newSet.has(pillarName)) {
        // Don't allow deselecting all pillars
        if (newSet.size > 1) {
          newSet.delete(pillarName)
        }
      } else {
        newSet.add(pillarName)
      }
      return newSet
    })
  }

  // Phase 3.1: Handle tactic reorder within a pillar
  const handleTacticReorder = useCallback(async (pillarIndex: number, newTactics: MECETactic[]) => {
    // Update the data with new tactic order
    const updatedPillars = data.pillars.map((pillar, idx) => {
      if (idx === pillarIndex) {
        return {
          ...pillar,
          tactics: newTactics
        }
      }
      return pillar
    })

    // Save to backend
    try {
      await dispatch(updateWorkspaceAsync({
        conversationId,
        updates: {
          mece_table: {
            ...data,
            pillars: updatedPillars,
            mece_score: null  // Invalidate score after reorder
          }
        }
      })).unwrap()

      console.log('[MECETableEditor] Tactic order saved successfully')
    } catch (error: any) {
      console.error('[MECETableEditor] Failed to save tactic order:', error)
    }
  }, [data, conversationId, dispatch])

  // Phase 3.3: Handle tactic status change
  const handleTacticStatusChange = useCallback(async (
    pillarIndex: number,
    tacticIndex: number,
    status: ImplementationStatus
  ) => {
    const pillar = data.pillars[pillarIndex]
    const tactic = pillar?.tactics?.[tacticIndex]
    const tacticId = (tactic as any)?.initiative_id ||
                     `pillar_${pillarIndex}_tactic_${tacticIndex}_${tactic?.title?.slice(0, 20) || 'unnamed'}`

    try {
      // Update backend
      await omapApi.updateTacticStatus(conversationId, tacticId, status)

      // Update local state via Redux
      const updatedPillars = data.pillars.map((p, pIdx) => {
        if (pIdx === pillarIndex) {
          return {
            ...p,
            tactics: p.tactics.map((t, tIdx) => {
              if (tIdx === tacticIndex) {
                return { ...t, implementation_status: status }
              }
              return t
            })
          }
        }
        return p
      })

      await dispatch(updateWorkspaceAsync({
        conversationId,
        updates: {
          mece_table: {
            ...data,
            pillars: updatedPillars
          }
        }
      })).unwrap()

      console.log(`[MECETableEditor] Status updated: ${tacticId} -> ${status}`)
    } catch (error: any) {
      console.error('[MECETableEditor] Failed to update tactic status:', error)
    }
  }, [data, conversationId, dispatch])

  const handleEditToggle = () => {
    if (isEditMode && isDirty) {
      const confirmCancel = window.confirm('You have unsaved changes. Are you sure you want to cancel?')
      if (!confirmCancel) return
    }

    if (!isEditMode) {
      setIsEditMode(true)
      setDraftPillars(data.pillars || [])
      setIsDirty(false)
      setValidationErrors([])
    } else {
      setIsEditMode(false)
      setDraftPillars(data.pillars || [])
      setIsDirty(false)
      setValidationErrors([])
    }
  }

  const handleSave = async () => {
    // Client-side validation
    const errors: string[] = []

    draftPillars.forEach((pillar, _idx) => {
      if (!pillar.tactics || pillar.tactics.length === 0) {
        errors.push(`Pillar "${pillar.name}" must have at least one initiative`)
      }

      pillar.tactics.forEach((tactic, tacticIdx) => {
        if (!tactic.title || tactic.title.trim().length < 3) {
          errors.push(`Pillar "${pillar.name}", Initiative #${tacticIdx + 1}: Title is too short (min 3 characters)`)
        }
        if (!tactic.description || tactic.description.trim().length < 10) {
          errors.push(`Pillar "${pillar.name}", Initiative "${tactic.title}": Description is too short (min 10 characters)`)
        }
      })
    })

    if (errors.length > 0) {
      setValidationErrors(errors)
      return
    }

    setIsSaving(true)
    setValidationErrors([])

    try {
      const updatedMeceTable = {
        ...data,
        pillars: draftPillars,
        mece_score: null  // Invalidate score after user edits
      }

      const result = await dispatch(updateWorkspaceAsync({
        conversationId,
        updates: { mece_table: updatedMeceTable }
      })).unwrap()

      setIsEditMode(false)
      setIsDirty(false)
      setIsSaving(false)
      onSaveSuccess?.()

      // Auto-sync Analysis Report (non-blocking)
      const fullReport = activeConversation?.analysisReportData?.fullReport
      if (fullReport) {
        setSyncStatus('syncing')
        omapApi.syncStrategyReport(
          conversationId,
          fullReport,
          result.updatedWorkspace || { mece_table: updatedMeceTable },
          'mece'
        ).then(syncResult => {
          dispatch(updateAnalysisReport({
            conversationId,
            data: { fullReport: syncResult.document }
          }))
          setSyncStatus('synced')
          setTimeout(() => setSyncStatus('idle'), 2000)
        }).catch(err => {
          console.warn('[MECETableEditor] Report sync failed (save succeeded):', err)
          setSyncStatus('failed')
          setTimeout(() => setSyncStatus('idle'), 3000)
        })
      }
    } catch (error: any) {
      const errorMessage = error.message || 'Failed to save MECE table changes'
      setValidationErrors([errorMessage])
      setIsSaving(false)
    }
  }

  const handleAddPillar = () => {
    const newPillar: MECEPillar = {
      name: 'New Pillar',
      description: '',
      priority_rank: draftPillars.length + 1,
      tactics: [
        {
          title: '',
          description: '',
          priority: 1,
          relevance_score: 0,
        }
      ]
    }
    setDraftPillars([...draftPillars, newPillar])
    setIsDirty(true)
  }

  const handleUpdatePillar = (
    pillarIdx: number,
    field: 'name' | 'description',
    value: string
  ) => {
    const updatedPillars = draftPillars.map((p, idx) =>
      idx === pillarIdx ? { ...p, [field]: value } : p
    )
    setDraftPillars(updatedPillars)
    setIsDirty(true)
  }

  const handleDeletePillar = (pillarIdx: number) => {
    const pillar = draftPillars[pillarIdx]
    if (!pillar) return
    const tacticCount = pillar.tactics?.length || 0
    const cascadeSummary = [
      `Delete pillar ${pillarIdx + 1} '${pillar.name}'? This will remove ${tacticCount} tactic(s).`,
      `Cannot be undone.`
    ].join('\n\n')
    if (!window.confirm(cascadeSummary)) return
    const updated = draftPillars.filter((_, i) => i !== pillarIdx)
    // Renumber priority_rank to stay contiguous (1-indexed)
    const renumbered: MECEPillar[] = updated.map((p, i) => ({
      ...p,
      priority_rank: i + 1,
    }))
    setDraftPillars(renumbered)
    setIsDirty(true)
  }

  const handleAddTactic = (pillarIdx: number) => {
    const newTactic: MECETactic = {
      title: 'New Tactic',
      description: 'Describe this tactic.',
      rationale: '',
      node_ids: [],
      priority: draftPillars[pillarIdx].tactics.length + 1,
      relevance_score: 0
    }

    const updatedPillars = draftPillars.map((p, idx) => {
      if (idx === pillarIdx) {
        return {
          ...p,
          tactics: [...p.tactics, newTactic]
        }
      }
      return p
    })

    setDraftPillars(updatedPillars)
    setIsDirty(true)
  }

  const handleUpdateTactic = (
    pillarIdx: number,
    tacticIdx: number,
    field: 'title' | 'description' | 'rationale',
    value: string
  ) => {
    const updatedPillars = draftPillars.map((p, pIdx) => {
      if (pIdx === pillarIdx) {
        return {
          ...p,
          tactics: p.tactics.map((t, tIdx) =>
            tIdx === tacticIdx ? { ...t, [field]: value } : t
          )
        }
      }
      return p
    })

    setDraftPillars(updatedPillars)
    setIsDirty(true)
  }

  const handleDeleteTactic = (pillarIdx: number, tacticIdx: number) => {
    const pillar = draftPillars[pillarIdx]
    const tactic = pillar?.tactics?.[tacticIdx]

    // Calculate tactic reference (Jan 2026)
    const pillarRank = pillar?.priority_rank ?? (pillarIdx + 1)
    const tacticPriority = tactic?.priority ?? (tacticIdx + 1)
    const tacticRef = `${pillarRank}.${tacticPriority}`

    // Check for downstream impact in Strategy Map (Jan 2026)
    const referencingInitiatives = checkTacticReferencedInStrategyMap(tacticRef, strategyMapData)

    let confirmMessage = 'Delete this initiative?'
    if (referencingInitiatives.length > 0) {
      confirmMessage = `This initiative (${tacticRef}) is referenced by ${referencingInitiatives.length} Strategy Map initiative(s): ${referencingInitiatives.join(', ')}.\n\nRemoving it will create a completeness violation in the Strategy Map.\n\nContinue with deletion?`
    }

    const confirmed = window.confirm(confirmMessage)
    if (!confirmed) return

    const updatedPillars = draftPillars.map((p, pIdx) => {
      if (pIdx === pillarIdx) {
        return {
          ...p,
          tactics: p.tactics.filter((_, tIdx) => tIdx !== tacticIdx)
        }
      }
      return p
    })

    setDraftPillars(updatedPillars)
    setIsDirty(true)
  }

  // Phase 6.5: Scenario handlers
  const handleTacticInclusionChange = (pillarIdx: number, tacticIdx: number, state: TacticInclusionState) => {
    if (!activeScenario) return
    dispatch(updateTacticInclusion({
      conversationId,
      scenarioId: activeScenario.id,
      pillarIdx,
      tacticIdx,
      inclusion: state
    }))
  }

  const handleCreateScenario = (scenario: WhatIfScenario) => {
    dispatch(createScenarioAction({
      conversationId,
      name: scenario.name,
      fromBaseline: !scenario.tactic_selections.every(s => s.inclusion === 'maybe'),
      meceTable: data
    }))
  }

  const handleDeleteScenario = (scenarioId: string) => {
    dispatch(deleteScenarioAction({ conversationId, scenarioId }))
  }

  const handleRenameScenario = (scenarioId: string, newName: string) => {
    dispatch(renameScenarioAction({ conversationId, scenarioId, newName }))
  }

  const handleSelectScenario = (scenarioId: string) => {
    dispatch(selectScenarioAction({ conversationId, scenarioId }))
  }

  const handleToggleComparison = () => {
    dispatch(toggleComparisonModeAction({ conversationId }))
  }

  if (!data.pillars || data.pillars.length === 0) {
    return (
      <div className="mece-table-editor-simple">
        <div className="no-data-message">
          No MECE table data available.
        </div>
      </div>
    )
  }

  if (!isEditMode) {
    return (
      <div className="mece-table-editor-simple">
        <div className="editor-header">
          {!isScenarioMode && !isReorderMode && (
            <button
              className="btn-edit"
              onClick={handleEditToggle}
              disabled={isSampleCompany}
              title={isSampleCompany ? SAMPLE_COMPANY_TOOLTIP : undefined}
            >
              Edit MECE Table
            </button>
          )}

          {/* Phase 3.1: Reorder Mode Toggle */}
          {!isScenarioMode && !isSelectionMode && (
            <button
              className={`btn-reorder-mode ${isReorderMode ? 'active' : ''}`}
              onClick={() => setIsReorderMode(!isReorderMode)}
              disabled={isSampleCompany}
              title={isSampleCompany ? SAMPLE_COMPANY_TOOLTIP : 'Toggle priority reorder mode'}
            >
              <span className="icon"></span>
              {isReorderMode ? 'Done Reordering' : 'Reorder Priorities'}
            </button>
          )}

          {/* Phase 1.1: Pillar Selection Mode Toggle */}
          {isSelectionModeEnabled && !isScenarioMode && !isReorderMode && (
            <button
              className={`btn-selection-mode ${isSelectionMode ? 'active' : ''}`}
              onClick={() => setIsSelectionMode(!isSelectionMode)}
              disabled={isSampleCompany}
              title={isSampleCompany ? SAMPLE_COMPANY_TOOLTIP : 'Toggle pillar selection mode for Strategy Map'}
            >
              <span className="icon">✓</span>
              {isSelectionMode ? 'Done Selecting' : 'Select Pillars'}
            </button>
          )}

          {/* Phase 1.1: Selection summary when in selection mode */}
          {isSelectionMode && (
            <span className="selection-summary">
              {selectedPillars.size} of {data.pillars.length} pillars selected
            </span>
          )}

          {/* Phase 3.1: Reorder mode indicator */}
          {isReorderMode && (
            <span className="reorder-summary">
              Drag initiatives to reorder priorities
            </span>
          )}

          {/* Phase 3.4: Export Playbook Button */}
          {!isEditMode && !isSelectionMode && !isReorderMode && (
            <button
              className="btn-export-playbook"
              onClick={() => setIsExportModalOpen(true)}
              title="Export strategy playbook document"
            >
              Export Playbook
            </button>
          )}
        </div>

        {/* Phase 6.5: Scenario Selector Toolbar */}
        {isScenarioMode && (
          <div className="scenario-toolbar">
            <ScenarioSelector
              scenarios={scenarios}
              activeScenarioId={activeScenario?.id || null}
              meceTable={data}
              onSelectScenario={handleSelectScenario}
              onCreateScenario={handleCreateScenario}
              onDeleteScenario={handleDeleteScenario}
              onRenameScenario={handleRenameScenario}
              onToggleComparison={handleToggleComparison}
              comparisonMode={comparisonMode}
            />
          </div>
        )}

        <MECETableRenderer
          data={data}
          onQuickAsk={onQuickAsk}
          isScenarioMode={isScenarioMode}
          activeScenario={activeScenario}
          onTacticInclusionChange={handleTacticInclusionChange}
          isSelectionMode={isSelectionMode}
          selectedPillars={selectedPillars}
          onPillarSelectionToggle={handlePillarSelectionToggle}
          isReorderMode={isReorderMode}
          onTacticReorder={handleTacticReorder}
          onTacticStatusChange={handleTacticStatusChange}
        />

        {/* Phase 3.4: Export Playbook Modal */}
        <ExportPlaybookModal
          isOpen={isExportModalOpen}
          conversationId={conversationId}
          onClose={() => setIsExportModalOpen(false)}
        />

        {/* Report sync status toast */}
        {syncStatus === 'syncing' && (
          <div className="sync-toast syncing">Syncing report...</div>
        )}
        {syncStatus === 'synced' && (
          <div className="sync-toast synced">Report updated</div>
        )}
        {syncStatus === 'failed' && (
          <div className="sync-toast failed">Report sync failed (changes saved)</div>
        )}
      </div>
    )
  }

  return (
    <div className="mece-table-editor-simple edit-mode">
      <div className="editor-toolbar">
        <div className="toolbar-status">
          {isDirty && <span className="status-badge">Unsaved Changes</span>}
          {isSaving && <span className="status-badge saving">Saving...</span>}
        </div>
        <div className="toolbar-actions">
          <button
            className="btn-save"
            onClick={handleSave}
            disabled={isSaving || !isDirty}
          >
            Save Changes
          </button>
          <button
            className="btn-cancel"
            onClick={handleEditToggle}
            disabled={isSaving}
          >
            Cancel
          </button>
        </div>
      </div>

      {validationErrors.length > 0 && (
        <div className="validation-errors">
          <strong>⚠️ Validation Errors:</strong>
          <ul>
            {validationErrors.map((error, idx) => (
              <li key={idx}>{error}</li>
            ))}
          </ul>
        </div>
      )}

      <div className="pillars-editor">
        {draftPillars.map((pillar, pillarIdx) => (
          <div key={pillarIdx} className="pillar-section" data-testid={`pillar-row-${pillarIdx}`}>
            <div className="pillar-header">
              <input
                className="pillar-name-input"
                type="text"
                value={pillar.name}
                onChange={(e) => handleUpdatePillar(pillarIdx, 'name', e.target.value)}
                placeholder="Pillar name"
              />
              <button
                className="btn-add-tactic"
                onClick={() => handleAddTactic(pillarIdx)}
              >
                + Add Initiative
              </button>
              {isEditMode && (
                <button
                  data-testid={`pillar-delete-${pillarIdx}`}
                  className="pillar-delete-btn"
                  onClick={() => handleDeletePillar(pillarIdx)}
                  disabled={isSampleCompany}
                  title={isSampleCompany ? SAMPLE_COMPANY_TOOLTIP : 'Delete this pillar'}
                  aria-label={`Delete pillar ${pillarIdx + 1}`}
                >
                  🗑
                </button>
              )}
            </div>

            <textarea
              className="pillar-description-input"
              value={pillar.description}
              onChange={(e) => handleUpdatePillar(pillarIdx, 'description', e.target.value)}
              placeholder="Pillar description"
              rows={2}
            />

            <div className="tactics-list">
              {pillar.tactics.length === 0 ? (
                <div className="empty-tactics">
                  No initiatives. Click "+ Add Initiative" to add one.
                </div>
              ) : (
                pillar.tactics.map((tactic, tacticIdx) => (
                  <div key={tacticIdx} className="tactic-editor">
                    <div className="tactic-field">
                      <label>Title</label>
                      <input
                        type="text"
                        value={tactic.title}
                        onChange={(e) => handleUpdateTactic(
                          pillarIdx,
                          tacticIdx,
                          'title',
                          e.target.value
                        )}
                        placeholder="Enter initiative title"
                      />
                    </div>
                    <div className="tactic-field">
                      <label>Description</label>
                      <textarea
                        value={tactic.description}
                        onChange={(e) => handleUpdateTactic(
                          pillarIdx,
                          tacticIdx,
                          'description',
                          e.target.value
                        )}
                        placeholder="Describe this initiative"
                        rows={3}
                      />
                    </div>
                    <div className="tactic-field">
                      <label>Rationale (Optional)</label>
                      <textarea
                        value={tactic.rationale || ''}
                        onChange={(e) => handleUpdateTactic(
                          pillarIdx,
                          tacticIdx,
                          'rationale',
                          e.target.value
                        )}
                        placeholder="Why is this initiative relevant?"
                        rows={2}
                      />
                    </div>
                    <button
                      className="btn-delete-tactic"
                      onClick={() => handleDeleteTactic(pillarIdx, tacticIdx)}
                    >
                      Delete
                    </button>
                  </div>
                ))
              )}
            </div>
          </div>
        ))}

        {isEditMode && (
          <button
            className="add-pillar-btn"
            onClick={handleAddPillar}
            disabled={isSampleCompany}
            title={isSampleCompany ? SAMPLE_COMPANY_TOOLTIP : 'Add a new pillar'}
          >
            + Add Pillar
          </button>
        )}
      </div>
    </div>
  )
}
